// ============================================================
//  src/App.js
// ============================================================
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

import AppLayout          from './components/layout/AppLayout';
import LoginPage          from './pages/auth/LoginPage';
import RegisterPage       from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import VerifyEmailPage    from './pages/auth/VerifyEmailPage';
import DashboardPage      from './pages/dashboard/DashboardPage';
import BotDetailPage      from './pages/bots/BotDetailPage';
import HowToUsePage       from './pages/HowToUsePage';
import MetaVerificationWizard from './pages/MetaVerificationWizard';
import ProfilePage        from './pages/ProfilePage';
import CollaborationsPage from './pages/CollaborationsPage';
import CommunityPage      from './pages/community/CommunityPage';
import BillingPage        from './pages/billing/BillingPage';
import RentSpacePage      from './pages/rent-space/RentSpacePage';
import BillingCallbackPage from './pages/billing/BillingCallbackPage';
import IntegrationsPage   from './pages/integrations/IntegrationsPage';
import LandingPage        from './pages/LandingPage';
import WebhookSettingsPage from './pages/settings/WebhookSettingsPage';
import ContactPage          from './pages/ContactPage';
import AgentLoginPage     from './pages/agent/AgentLoginPage';
import AgentConsolePage   from './pages/agent/AgentConsolePage';
import InboxPickerPage    from './pages/inbox/InboxPickerPage';
import InboxBotPage       from './pages/inbox/InboxBotPage';

// ── Admin (/bot-father) ────────────────────────────────────
import { AdminAuthProvider, useAdminAuth } from './admin/context/AdminAuthContext';
import AdminLayout          from './admin/components/AdminLayout';
import AdminLoginPage       from './admin/pages/AdminLoginPage';
import AdminDashboardPage   from './admin/pages/AdminDashboardPage';
import AdminUsersPage       from './admin/pages/AdminUsersPage';
import AdminUserDetailPage  from './admin/pages/AdminUserDetailPage';
import AdminBotsPage        from './admin/pages/AdminBotsPage';
import AdminBotDetailPage   from './admin/pages/AdminBotDetailPage';
import AdminPlansPage       from './admin/pages/AdminPlansPage';
import AdminPaymentsPage    from './admin/pages/AdminPaymentsPage';
import AdminPocketfiPage    from './admin/pages/AdminPocketfiPage';
import AdminRentSpacePage   from './admin/pages/AdminRentSpacePage';
import AdminLogsPage        from './admin/pages/AdminLogsPage';
import AdminBroadcastsPage  from './admin/pages/AdminBroadcastsPage';
import AdminAdminsPage      from './admin/pages/AdminAdminsPage';

function FullPageSpinner() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 10, height: '100vh', color: 'var(--text-muted)', fontSize: 14,
    }}>
      <span style={{
        display: 'inline-block', width: 20, height: 20,
        border: '2.5px solid var(--border)', borderTopColor: 'var(--primary)',
        borderRadius: '50%', animation: 'spin 0.7s linear infinite',
      }} />
      Loading…
    </div>
  );
}

function Placeholder({ title }) {
  return (
    <div style={{ padding: 40, maxWidth: 720, margin: '0 auto' }}>
      <div className="page-card">
        <div className="page-card-hdr">{title}</div>
        <div className="page-card-body" style={{ color: 'var(--text-muted)' }}>
          This page hasn't been built yet.
        </div>
      </div>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return user ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return user ? <Navigate to="/dashboard" replace /> : children;
}

function LandingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return user ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

// ── Agent portal route guard (separate session from subscribers) ──
// No context/provider needed here -- just a token in localStorage,
// checked by AGENT_API's own interceptor (see src/api/client.js).
function AgentPrivateRoute({ children }) {
  const hasToken = !!localStorage.getItem('agentAuthToken');
  return hasToken ? children : <Navigate to="/agent" replace />;
}

// ── Admin route guards (/bot-father) ───────────────────────
function AdminPrivateRoute({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <FullPageSpinner />;
  return admin ? children : <Navigate to="/bot-father/login" replace />;
}

function AdminPublicRoute({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <FullPageSpinner />;
  return admin ? <Navigate to="/bot-father/dashboard" replace /> : children;
}

// The entire /bot-father tree gets its own AdminAuthProvider — kept
// independent from the subscriber-facing AuthProvider/AuthContext.
function AdminApp() {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="login" element={<AdminPublicRoute><AdminLoginPage /></AdminPublicRoute>} />
        <Route element={<AdminPrivateRoute><AdminLayout /></AdminPrivateRoute>}>
          <Route path="dashboard"     element={<AdminDashboardPage />} />
          <Route path="users"         element={<AdminUsersPage />} />
          <Route path="users/:sId"    element={<AdminUserDetailPage />} />
          <Route path="bots"          element={<AdminBotsPage />} />
          <Route path="bots/:bId"     element={<AdminBotDetailPage />} />
          <Route path="broadcasts"    element={<AdminBroadcastsPage />} />
          <Route path="plans"         element={<AdminPlansPage />} />
          <Route path="payments"      element={<AdminPaymentsPage />} />
          <Route path="pocketfi"      element={<AdminPocketfiPage />} />
          <Route path="rent-space"    element={<AdminRentSpacePage />} />
          <Route path="logs"          element={<AdminLogsPage />} />
          <Route path="admins"        element={<AdminAdminsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/bot-father/dashboard" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="bottom-center"
          toastOptions={{
            duration: 4000,
            style: {
              fontFamily: 'Bricolage Grotesque, sans-serif',
              fontSize: '13.5px',
              fontWeight: 600,
              borderRadius: 12,
            },
          }}
        />
        <Routes>
          {/* Public */}
          <Route path="/"                element={<LandingRoute />} />
          <Route path="/login"           element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register"        element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/verify-email"    element={<VerifyEmailPage />} />

          {/* Admin (separate auth, separate layout) */}
          <Route path="/bot-father/*" element={<AdminApp />} />

          {/* Agent portal (separate auth, no platform account needed) */}
          <Route path="/agent"         element={<AgentLoginPage />} />
          <Route path="/agent/console" element={<AgentPrivateRoute><AgentConsolePage /></AgentPrivateRoute>} />

          {/* Protected */}
          <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
            <Route path="dashboard"         element={<DashboardPage />} />
            <Route path="inbox"             element={<InboxPickerPage />} />
            <Route path="inbox/:bId"        element={<InboxBotPage />} />
            <Route path="how-to-use"        element={<HowToUsePage />} />
            <Route path="meta-verification" element={<MetaVerificationWizard />} />
            <Route path="bots/:bId/*"       element={<BotDetailPage />} />
            <Route path="community"         element={<CommunityPage />} />
            <Route path="collaborations"    element={<CollaborationsPage />} />
            <Route path="billing"           element={<BillingPage />} />
            <Route path="rent-space"        element={<RentSpacePage />} />
            <Route path="integrations"      element={<IntegrationsPage />} />
            <Route path="transactions"      element={<Placeholder title="Transactions" />} />
            <Route path="profile"           element={<ProfilePage />} />
            <Route path="settings/webhook"  element={<WebhookSettingsPage />} />
            <Route path="contact"           element={<ContactPage />} />
            <Route path="support"           element={<Placeholder title="Support" />} />
          </Route>

          {/* Payment callback — outside PrivateRoute so PocketFi redirect works */}
          <Route path="billing/callback" element={<BillingCallbackPage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}